import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import ayerisLogo from "@/assets/ayeris-logo.png";
import { useTranslation } from "react-i18next";
import AgeGateModal, { isAgeLocked } from "@/components/AgeGateModal";
import AgeBlockedScreen from "@/components/AgeBlockedScreen";

const DemoEnd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [ageBlocked, setAgeBlocked] = useState(() => isAgeLocked());

  const handleCreateAccount = () => {
    if (isAgeLocked()) { setAgeBlocked(true); return; }
    const alreadyVerified = localStorage.getItem("ayeris_birth_month");
    if (alreadyVerified) {
      navigate("/landing", { state: { view: "signup" } });
    } else {
      setShowAgeGate(true);
    }
  };

  if (ageBlocked) return <AgeBlockedScreen />;

  if (showAgeGate) {
    return (
      <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-4 py-6">
        <AgeGateModal
          onComplete={() => navigate("/landing", { state: { view: "signup" } })}
          onBlocked={() => setAgeBlocked(true)}
        />
        <button onClick={() => setShowAgeGate(false)} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors">
          ← {t("common.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-4 py-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20" style={{ background: "radial-gradient(ellipse, hsl(231 99% 62% / 0.4), transparent 70%)" }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] rounded-full opacity-10" style={{ background: "radial-gradient(ellipse, hsl(260 90% 60% / 0.4), transparent 70%)" }} />
      </div>

      <motion.div initial={{ scale: 0.9, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }} className="relative z-10 w-full max-w-sm space-y-8 text-center">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}>
          <img src={ayerisLogo} alt="Ayeris" className="w-24 h-24 rounded-3xl shadow-iris mx-auto" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
          <h2 className="font-display text-3xl font-bold text-foreground">{t("demoEnd.readyToTry")}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px] mx-auto">{t("demoEnd.createAccountDesc")}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="space-y-3">
          <button onClick={handleCreateAccount} className="w-full py-3.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" /> {t("demoEnd.createAccount")}
          </button>
          <button onClick={() => navigate("/landing", { state: { view: "signin" } })} className="w-full py-3 rounded-xl bg-secondary text-foreground font-display font-semibold text-sm border border-border active:scale-95 transition-transform flex items-center justify-center gap-2">
            {t("demoEnd.alreadyHaveAccount")} <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate("/landing")} className="w-full py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {t("demoEnd.backToHome")}
          </button>
        </motion.div>

        <div className="flex items-center justify-center gap-2 pt-2">
          <img src={ayerisLogo} alt="Ayeris" className="w-5 h-5 rounded" />
          <span className="font-display text-sm font-semibold text-foreground">Ayeris</span>
        </div>
      </motion.div>
    </div>
  );
};

export default DemoEnd;
